import { OccupantByPermission, PropertyByPermissionAPI, PropertyOccupant, UserPermissions } from '@centecomm/portal-service';
import { useProperty } from '@centecomm/portal-service-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlexWrapper } from 'Shared/FlexWrapper';
import ModalWithAction from 'Shared/Modal/ModalWithAction';
import { DropDownSelector } from 'Shared/PropertyTenantResolution';
import styles from './PropertyOccupantSelection.module.css';

interface PropertyOccupantSelectionProps {
    show: boolean;
    setShow: (show: boolean) => void;
    modalHeader: string;
    setPropertyId: (propertyId: number | null) => void;
    setOccupantId: (occupantId: number | null) => void;
    propertyId: number | null;
    occupantId: number | null;
    userPermission: UserPermissions;
    occupantFilter?: (occupant: PropertyOccupant) => boolean;
    occupantsPlaceholder?: string;
    noOccupantsPlaceholder?: string;
    setLoading: (loading: boolean) => void;
    setHasNoProperties: (noOccupants: boolean) => void;
}

const listContainerStyle: React.CSSProperties = {
    overflowY: 'scroll',
    maxHeight: '170px',
    padding: '0 0.5em',
};

function PropertyOccupantSelectionModal({
    show,
    setShow,
    modalHeader,
    setPropertyId,
    setOccupantId,
    propertyId,
    occupantId,
    userPermission,
    occupantFilter = () => true,
    occupantsPlaceholder,
    noOccupantsPlaceholder,
    setLoading,
    setHasNoProperties,
}: PropertyOccupantSelectionProps): React.ReactElement | null {
    const { t } = useTranslation(['common', 'occupants']);
    const [availableProperties, setAvailableProperties] = React.useState<PropertyByPermissionAPI[]>([]);
    const [availableOccupants, setAvailableOccupants] = React.useState<{ [propertyId: number]: OccupantByPermission[] }>({});
    const [propertiesLoading, setPropertiesLoading] = React.useState<boolean>(false);
    const [occupantsLoading, setOccupantsLoading] = React.useState<boolean>(false);

    const { getPropertiesByPermission, getOccupantsByPermission } = useProperty();

    const fetchOccupants = async () => {
        try {
            if (!propertyId) return;
            setOccupantsLoading(true);
            setOccupantId(null);

            if (!availableOccupants[propertyId]) {
                const occupantsResponse = await getOccupantsByPermission(propertyId, userPermission);
                const filteredOccupants = occupantsResponse.filter(occupantFilter);
                setAvailableOccupants((prev) => ({ ...prev, [propertyId]: filteredOccupants }));

                if (filteredOccupants.length === 1) {
                    setOccupantId(filteredOccupants[0].id);
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setOccupantsLoading(false);
        }
    };

    const fetchProperties = async () => {
        try {
            setPropertiesLoading(true);
            setLoading(true);
            const propertiesResponse = await getPropertiesByPermission(userPermission);

            if (propertiesResponse.length === 1) {
                setPropertyId(propertiesResponse[0].id);
            }
            if (propertiesResponse.length === 0) {
                setHasNoProperties(true);
            }

            setAvailableProperties(propertiesResponse);
        } catch (e) {
            console.error(e);
        } finally {
            setPropertiesLoading(false);
            setLoading(false);
        }
    };
    React.useEffect(() => {
        if (propertyId) fetchOccupants();
    }, [propertyId]);

    React.useEffect(() => {
        if (userPermission && !propertiesLoading) fetchProperties();
    }, [userPermission]);

    return show ? (
        <ModalWithAction
            showActionButton={false}
            header={modalHeader}
            cancelCallback={() => {
                setShow(false);
            }}
            modalWidth={{ width: '40rem' }}
        >
            <FlexWrapper align="start" justify="center" wrap column fullWidth style={{ padding: '1rem' }}>
                <p className={styles.MockLabel}>{t('occupants:dropdownSelector.placeholderWithProperty')}</p>
                <div className={styles.Container}>
                    <DropDownSelector
                        availableOptions={availableProperties}
                        selectedOptionIds={[propertyId || 0]}
                        visibleOptions={availableProperties.map((_) => _.id)}
                        selectOptionHandler={(ids) => setPropertyId(ids.pop() as Required<number>)}
                        loaded={!propertiesLoading}
                        hideToggleIcon
                        hideSearch
                        listContainerStyle={listContainerStyle}
                    />
                    <div className={styles.Separator} />
                    {propertyId && (
                        <DropDownSelector
                            availableOptions={availableOccupants[propertyId] || []}
                            selectedOptionIds={[occupantId || 0]}
                            visibleOptions={availableOccupants[propertyId]?.map((_) => _.id) || []}
                            selectOptionHandler={(ids) => setOccupantId(ids.pop() as Required<number>)}
                            loaded={!occupantsLoading}
                            hideToggleIcon
                            hideSearch
                            loadingMessage={`${t('common:loading')}...`}
                            placeholderOverride={
                                (availableOccupants[propertyId]?.length === 0 && noOccupantsPlaceholder) ||
                                occupantsPlaceholder ||
                                t('occupants:dropdownSelector.placeholder')
                            }
                            listContainerStyle={listContainerStyle}
                        />
                    )}
                </div>
            </FlexWrapper>
        </ModalWithAction>
    ) : null;
}

export default PropertyOccupantSelectionModal;
